/**
 * Someone left a call we're in. If nobody else is connected to us, the call
 * is over: the server ends it too (D35), but a caller dropping mid-call used
 * to leave the other person alone in it, so the app doesn't rely on that.
 */
export function aloneAfterLeaving(
  inCall: boolean,
  peersRemaining: number
): boolean {
  return inCall && peersRemaining === 0;
}

// ---------------------------------------------------------------------------
// Weak connections (D225–D226, docs/calls-round-two.md §6). Calls are mesh, so
// every phone uploads one copy of its video per other person (issue #38).

/** How often useCall reads each video sender's stats. */
export const VIDEO_STATS_INTERVAL_MS = 2_000;
/** Weak for this long → act on it (D225). */
export const VIDEO_WEAK_FOR_MS = 8_000;
/** Good for this long → suggest video again, or lift a lowered cap (D225). */
export const VIDEO_GOOD_FOR_MS = 20_000;
/**
 * A connection's first seconds after it connects (or reconnects) don't count
 * as weak: the bandwidth estimate starts low and ramps up.
 */
export const VIDEO_WARMUP_MS = 10_000;
/** A gap longer than this (a throttled tab) breaks a run of samples. */
export const VIDEO_SAMPLE_GAP_MS = 5_000;
/** Packet loss above this fraction can't carry video. */
export const VIDEO_WEAK_LOSS = 0.15;
/** Loss at or below this counts as recovered. */
export const VIDEO_GOOD_LOSS = 0.05;
/**
 * Video's floor: an upload estimate under this (or under our own cap, if
 * lower) can't carry it. Also the lowest a weak connection's cap goes.
 */
export const VIDEO_FLOOR_BITRATE = 150_000;
/** Estimated upload needed to call a connection recovered. */
export const VIDEO_RECOVER_BITRATE = 400_000;

/** One reading of the video going to one other person. */
export interface VideoLinkSample {
  /** ms timestamp of the reading. */
  at: number;
  /** `outbound-rtp.qualityLimitationReason` ('none', 'bandwidth', 'cpu'…). */
  qualityLimitationReason: string | null;
  /** `candidate-pair.availableOutgoingBitrate`, bits per second. */
  availableOutgoingBitrate: number | null;
  /** `outbound-rtp.targetBitrate`, bits per second. */
  targetBitrate: number | null;
  /** Measured from `bytesSent` between two readings, bits per second. */
  sentBitrate: number | null;
  /** `remote-inbound-rtp.fractionLost`, 0–1. */
  packetLoss: number | null;
}

/**
 * Can't carry video (D225): heavy loss, or the sender is bandwidth-limited
 * (or sending under half its target) **and** the upload estimate is below the
 * floor. Frame size and the limitation reason alone never count: our own
 * group cap (D226) makes the browser scale down and report `bandwidth` on a
 * perfectly good network. `cap` is this sender's current `maxBitrate`.
 */
export function isWeakVideoSample(
  sample: VideoLinkSample,
  cap: number = Infinity
): boolean {
  if (sample.packetLoss !== null && sample.packetLoss > VIDEO_WEAK_LOSS) {
    return true;
  }
  const bandwidthLimited =
    sample.qualityLimitationReason === 'bandwidth' ||
    (sample.targetBitrate !== null &&
      sample.sentBitrate !== null &&
      sample.targetBitrate > 0 &&
      sample.sentBitrate < sample.targetBitrate * 0.5);
  const atFloor =
    sample.availableOutgoingBitrate !== null &&
    sample.availableOutgoingBitrate < Math.min(cap, VIDEO_FLOOR_BITRATE);
  return bandwidthLimited && atFloor;
}

/**
 * Good enough to suggest video again. With the camera paused the encoder sends
 * almost nothing, so only loss and the upload estimate say anything; a sample
 * with neither is unknown, not good.
 */
export function isGoodVideoSample(
  sample: VideoLinkSample,
  cap: number = Infinity
): boolean {
  const { packetLoss, availableOutgoingBitrate } = sample;
  if (packetLoss === null && availableOutgoingBitrate === null) return false;
  if (packetLoss !== null && packetLoss > VIDEO_GOOD_LOSS) return false;
  // A copy we capped low can't report an estimate above its cap, so a
  // lowered connection counts as good once it fills most of that cap.
  const enough = Math.min(VIDEO_RECOVER_BITRATE, cap * 0.9);
  if (
    availableOutgoingBitrate !== null &&
    availableOutgoingBitrate < enough
  ) {
    return false;
  }
  return true;
}

/** How long the newest samples have all matched `test`, without gaps. */
function trailingRunMs(
  samples: readonly VideoLinkSample[],
  test: (sample: VideoLinkSample) => boolean
): number {
  const last = samples[samples.length - 1];
  if (!last || !test(last)) return 0;
  let first = last;
  for (let i = samples.length - 2; i >= 0; i--) {
    const sample = samples[i] as VideoLinkSample;
    if (!test(sample) || first.at - sample.at > VIDEO_SAMPLE_GAP_MS) break;
    first = sample;
  }
  return last.at - first.at;
}

/** Recent readings for the connection to one other person. */
export interface VideoConnectionHistory {
  /** When it last became connected; starts the warm-up. */
  connectedAt: number;
  /** Its sender's current `maxBitrate`. */
  cap: number;
  /** Oldest first. */
  samples: VideoLinkSample[];
}

function weakForMs(connection: VideoConnectionHistory): number {
  const warm = connection.connectedAt + VIDEO_WARMUP_MS;
  return trailingRunMs(
    connection.samples.filter((s) => s.at >= warm),
    (s) => isWeakVideoSample(s, connection.cap)
  );
}

function goodForMs(connection: VideoConnectionHistory): number {
  return trailingRunMs(connection.samples, (s) =>
    isGoodVideoSample(s, connection.cap)
  );
}

/**
 * More than half of the connections; the only connection counts alone (D225).
 * One weak receiver in a three-person call lowers its own copy, not everyone's
 * camera.
 */
export function majorityOf(connections: number): number {
  return connections <= 1 ? 1 : Math.floor(connections / 2) + 1;
}

/** A lower cap for a weak connection: under its estimate, never below 150k. */
export function weakSenderBitrate(
  cap: number,
  availableOutgoingBitrate: number | null
): number {
  const target =
    availableOutgoingBitrate !== null
      ? Math.round(availableOutgoingBitrate * 0.8)
      : Math.round(cap / 2);
  return Math.max(VIDEO_FLOOR_BITRATE, Math.min(cap, target));
}

/**
 * - `watching`: video is on and being watched.
 * - `paused`: we turned the camera off; "Weak connection — video paused".
 * - `better`: still paused; "Connection better — turn video on?".
 * - `overridden`: the person chose for themselves; hands off for this call.
 */
export type VideoFallbackMode = 'watching' | 'paused' | 'better' | 'overridden';

export interface VideoFallbackState {
  mode: VideoFallbackMode;
  /** Connections (by user id) a minority weakness lowered, and their cap. */
  lowered: Record<string, number>;
}

export const INITIAL_VIDEO_FALLBACK: VideoFallbackState = {
  mode: 'watching',
  lowered: {},
};

export interface VideoFallbackDecision {
  state: VideoFallbackState;
  /**
   * `pause`: turn the camera off the same way the camera button does.
   * `suggest`: show "Connection better — turn video on?".
   */
  action: 'none' | 'pause' | 'suggest';
}

function sameCaps(a: Record<string, number>, b: Record<string, number>) {
  const keys = Object.keys(a);
  return (
    keys.length === Object.keys(b).length && keys.every((k) => a[k] === b[k])
  );
}

/**
 * D225: given each connected person's recent readings, what to do now. The
 * majority decides: when at least half the connections (rounding up) have
 * been weak for 8 s, the camera pauses; a weak minority only gets a lower
 * bitrate on their own copies, lifted after 20 s good. Never turns video back
 * on by itself, so it can't flap; never touches audio.
 */
export function videoFallbackDecision(
  connections: Readonly<Record<string, VideoConnectionHistory>>,
  state: VideoFallbackState
): VideoFallbackDecision {
  const stay: VideoFallbackDecision = { state, action: 'none' };
  const ids = Object.keys(connections);
  if (ids.length === 0 || state.mode === 'overridden') return stay;
  const needed = majorityOf(ids.length);
  const weak = ids.filter(
    (id) =>
      weakForMs(connections[id] as VideoConnectionHistory) >= VIDEO_WEAK_FOR_MS
  );

  switch (state.mode) {
    case 'watching': {
      if (weak.length >= needed) {
        return { state: { mode: 'paused', lowered: {} }, action: 'pause' };
      }
      const lowered: Record<string, number> = {};
      for (const id of ids) {
        const connection = connections[id] as VideoConnectionHistory;
        const current = state.lowered[id];
        if (weak.includes(id)) {
          const latest = connection.samples[connection.samples.length - 1];
          const next = weakSenderBitrate(
            connection.cap,
            latest?.availableOutgoingBitrate ?? null
          );
          lowered[id] = current === undefined ? next : Math.min(current, next);
        } else if (
          current !== undefined &&
          goodForMs(connection) < VIDEO_GOOD_FOR_MS
        ) {
          lowered[id] = current;
        }
      }
      return sameCaps(lowered, state.lowered)
        ? stay
        : { state: { mode: 'watching', lowered }, action: 'none' };
    }
    case 'paused': {
      const notGood = ids.filter(
        (id) =>
          goodForMs(connections[id] as VideoConnectionHistory) <
          VIDEO_GOOD_FOR_MS
      );
      // Suggest once a majority has recovered (the same majority that pauses).
      return ids.length - notGood.length >= needed
        ? { state: { mode: 'better', lowered: {} }, action: 'suggest' }
        : stay;
    }
    case 'better':
      // Worse again before they answered: take the suggestion back.
      return weak.length >= needed
        ? { state: { mode: 'paused', lowered: {} }, action: 'none' }
        : stay;
  }
}

/** A stats entry as `RTCStatsReport.values()` yields it. */
export type RtcStat = { type: string; id: string; [key: string]: unknown };

export interface SenderStatsReading {
  sample: Omit<VideoLinkSample, 'at'>;
  /** Kept to measure the sent bitrate on the next reading. */
  bytesSent: number | null;
  timestamp: number | null;
}

const num = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * Pick what D225 needs out of one video sender's `getStats()`: its
 * outbound-rtp, the matching remote-inbound-rtp, and the selected candidate
 * pair (through the transport, or the nominated succeeded pair).
 */
export function readVideoSenderStats(
  stats: Iterable<RtcStat>,
  previous: { bytesSent: number | null; timestamp: number | null } | null
): SenderStatsReading | null {
  const all = [...stats];
  const outbound = all.find(
    (s) =>
      s.type === 'outbound-rtp' &&
      (s.kind === 'video' || s.mediaType === 'video')
  );
  if (!outbound) return null;
  const remote = all.find(
    (s) =>
      s.type === 'remote-inbound-rtp' &&
      (s.localId === outbound.id || s.id === outbound.remoteId)
  );
  const transport = all.find((s) => s.type === 'transport');
  const pair =
    all.find(
      (s) =>
        s.type === 'candidate-pair' &&
        transport !== undefined &&
        s.id === transport.selectedCandidatePairId
    ) ??
    all.find(
      (s) =>
        s.type === 'candidate-pair' &&
        s.nominated === true &&
        s.state === 'succeeded'
    );

  const bytesSent = num(outbound.bytesSent);
  const timestamp = num(outbound.timestamp);
  let sentBitrate: number | null = null;
  if (
    previous &&
    previous.bytesSent !== null &&
    previous.timestamp !== null &&
    bytesSent !== null &&
    timestamp !== null &&
    timestamp > previous.timestamp &&
    bytesSent >= previous.bytesSent
  ) {
    sentBitrate =
      ((bytesSent - previous.bytesSent) * 8 * 1000) /
      (timestamp - previous.timestamp);
  }
  const reason = outbound.qualityLimitationReason;
  return {
    sample: {
      qualityLimitationReason: typeof reason === 'string' ? reason : null,
      availableOutgoingBitrate: num(pair?.availableOutgoingBitrate),
      targetBitrate: num(outbound.targetBitrate),
      sentBitrate,
      packetLoss: num(remote?.fractionLost),
    },
    bytesSent,
    timestamp,
  };
}

/** `navigator.connection` where the browser has it (Chrome, Android). */
export interface NetworkInformationLike {
  type?: string;
  effectiveType?: string;
}

/** D226: mobile data, or a link slow enough to treat as such. Unknown → Wi-Fi. */
export function isMobileData(
  connection: NetworkInformationLike | null | undefined
): boolean {
  if (!connection) return false;
  if (connection.type === 'cellular') return true;
  return ['slow-2g', '2g', '3g'].includes(connection.effectiveType ?? '');
}

/** Calls of this many people or more cap each sender lower (D226). */
export const GROUP_CALL_SIZE = 4;
export const WIFI_MAX_BITRATE = 900_000;
export const MOBILE_MAX_BITRATE = 350_000;
export const GROUP_MAX_BITRATE = 250_000;

export interface VideoCapture {
  width: number;
  height: number;
  /** Unset: the browser's default, as before D226. */
  frameRate?: number;
}

/** D226: the camera size to ask `getUserMedia` for at the start of a call. */
export function startingVideoCapture(
  connection: NetworkInformationLike | null | undefined
): VideoCapture {
  return isMobileData(connection)
    ? { width: 640, height: 360, frameRate: 24 }
    : { width: 1280, height: 720 };
}

/**
 * D226: `maxBitrate` for each video sender. Mesh uploads one copy per other
 * person, so from four people each copy is capped lower. Recomputed as people
 * join and leave.
 */
export function senderBitrateCap(
  connection: NetworkInformationLike | null | undefined,
  peopleInCall: number
): number {
  const base = isMobileData(connection) ? MOBILE_MAX_BITRATE : WIFI_MAX_BITRATE;
  return peopleInCall >= GROUP_CALL_SIZE
    ? Math.min(base, GROUP_MAX_BITRATE)
    : base;
}

/**
 * D226: in a group call, send a smaller picture so the lower cap isn't
 * starving a 720p encoder: scale down to about 360 lines.
 */
export function senderScaleDown(
  connection: NetworkInformationLike | null | undefined,
  peopleInCall: number
): number {
  if (peopleInCall < GROUP_CALL_SIZE) return 1;
  return Math.max(1, startingVideoCapture(connection).height / 360);
}
